import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronUp } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { HubDataTable, HubDataTextCell, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { CopyToClipboardGlyph } from "@/components/ui/CopyToClipboardGlyph";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintWithGlyph, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { HubSupportTicketAttachmentList } from "@/components/HubSupportTicketAttachmentList";
import {
  HubSupportTicketMessage,
  HubSupportTicketMessageThread,
  formatHubSupportTicketStatusLabel,
} from "@/components/HubSupportTicketMessageThread";
import {
  hubSupportTicketFieldLabelClassName,
  hubSupportTicketReviewerFieldCopy,
} from "@/lib/hubSupportTicketFieldCopy";
import {
  hubSupportTicketAllowsPendingFeedback,
  hubSupportTicketStatusMayClose,
} from "@/lib/hubSupportTicketStatus";
import { HUB_SUPPORT_TICKET_STATUS_META } from "@/lib/hubSupportTicketStatusMeta";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  computeHorizontalMenuPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { formatTimestamp } from "@/lib/utils";
import {
  hubSupportTicketAttachmentApiPayload,
  type HubSupportLocalAttachment,
  type HubSupportTicketAttachmentView,
} from "@/lib/hubSupportTicketAttachments";
import {
  formatCursorAgentCostUsd,
  formatCursorAgentTokenSummary,
  formatCursorAgentUsageFootnote,
  isLocalCursorAgentId,
  localAgentResumeDeeplink,
  localAgentResumeKeyphrase,
  type CursorAgentUsageQuality,
} from "@/lib/cursorAgentUsageDisplay";
import {
  formatNotificationItemOwnerLabel,
  NotificationItemOwnerControls,
} from "@/components/NotificationItemOwnerControls";

/**
 * Ticket file previews use blob URLs so large data URLs do not hit browser length limits.
 */
interface ListRow {
  id: string;
  created_at: string;
  updated_at: string;
  reporter_user_id: string;
  reporter_email: string;
  reporter_display_name: string;
  submission_source: "manual" | "automatic_client" | "system_automation";
  kind: string;
  status: string;
  closed: boolean;
  pending_feedback: boolean;
  page_path: string | null;
  subject: string | null;
  owner_user_id: string | null;
  owner_display_name: string | null;
}

interface DetailTicket {
  id: string;
  created_at: string;
  updated_at: string;
  reporter_user_id: string;
  reporter_email: string;
  reporter_display_name: string;
  submission_source: "manual" | "automatic_client" | "system_automation";
  kind: string;
  status: string;
  closed: boolean;
  pending_feedback: boolean;
  page_path: string | null;
  page_url_full: string | null;
  user_agent: string | null;
  actual_behavior: string;
  expected_behavior: string;
  console_capture: string | null;
  admin_notes: string | null;
  resolution_note: string | null;
  last_status_change_at: string | null;
  last_status_actor_user_id: string | null;
  last_status_actor_display_name: string | null;
  cursor_agent_status: string | null;
  cursor_agent_id: string | null;
  cursor_agent_run_id: string | null;
  cursor_agent_url: string | null;
  cursor_agent_model: string | null;
  cursor_agent_summary: string | null;
  cursor_agent_summary_detail: string | null;
  cursor_agent_tldr_problem: string | null;
  cursor_agent_tldr_cause: string | null;
  cursor_agent_tldr_fix: string | null;
  cursor_agent_pr_url: string | null;
  cursor_agent_branch_name: string | null;
  cursor_agent_duration_ms: string | null;
  cursor_agent_input_tokens: string | null;
  cursor_agent_output_tokens: string | null;
  cursor_agent_cache_read_tokens: string | null;
  cursor_agent_cache_write_tokens: string | null;
  cursor_agent_cost_usd: string | null;
  cursor_agent_usage_quality: CursorAgentUsageQuality | null;
  cursor_agent_error_message: string | null;
  cursor_agent_started_at: string | null;
  cursor_agent_completed_at: string | null;
  owner_user_id: string | null;
  owner_display_name: string | null;
}

interface DetailScreenshot extends HubSupportTicketAttachmentView {}

type TicketSortKey =
  | "id"
  | "created_at"
  | "reporter_display_name"
  | "owner_display_name"
  | "kind"
  | "status"
  | "page_path"
  | "subject";

const TICKET_URL_PARAM = "ticket";

function formatCursorAgentDurationMs(raw: string | null): string {
  if (raw == null || raw.trim() === "") {
    return "—";
  }
  const ms = Number.parseInt(raw, 10);
  if (!Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  if (ms < 60_000) {
    return `${Math.round(ms / 1000)}s`;
  }
  return `${Math.round(ms / 60_000)}m`;
}

function defaultStatusToggles(statuses: string[]): Record<string, boolean> {
  return Object.fromEntries(statuses.map((status) => [status, true]));
}

function readTicketSortValue(row: ListRow, key: TicketSortKey): string | number {
  switch (key) {
    case "id":
      return Number.parseInt(row.id, 10);
    case "created_at":
      return row.created_at;
    case "reporter_display_name":
      return row.reporter_display_name;
    case "owner_display_name":
      return row.owner_display_name ?? "";
    case "kind":
      return row.kind;
    case "status":
      return row.status;
    case "page_path":
      return row.page_path ?? "";
    case "subject":
      return row.subject ?? "";
    default:
      return "";
  }
}

function compareTicketRows(left: ListRow, right: ListRow, key: TicketSortKey, direction: SortDirection): number {
  const factor = direction === "asc" ? 1 : -1;
  const a = readTicketSortValue(left, key);
  const b = readTicketSortValue(right, key);
  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * factor;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }) * factor;
}

export function HubSupportTicketsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const statusMeta = useMemo(
    () => [...HUB_SUPPORT_TICKET_STATUS_META].sort((left, right) => left.sort_order - right.sort_order),
    [],
  );
  const statusKeys = useMemo(() => statusMeta.map((row) => row.status), [statusMeta]);
  const [statusEnabled, setStatusEnabled] = useState<Record<string, boolean>>(() =>
    defaultStatusToggles(HUB_SUPPORT_TICKET_STATUS_META.map((row) => row.status)),
  );
  const [queueActiveEnabled, setQueueActiveEnabled] = useState(true);
  const [queueArchivedEnabled, setQueueArchivedEnabled] = useState(false);
  const [showPendingFeedbackOnly, setShowPendingFeedbackOnly] = useState(false);
  const [sortKey, setSortKey] = useState<TicketSortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const detailId = searchParams.get(TICKET_URL_PARAM)?.trim() || null;
  const [draftStatus, setDraftStatus] = useState<string>("open");
  const [draftClosed, setDraftClosed] = useState(false);
  const [draftPendingFeedback, setDraftPendingFeedback] = useState(false);
  const [draftAdminNotes, setDraftAdminNotes] = useState<string>("");
  const [draftResolutionNote, setDraftResolutionNote] = useState<string>("");
  const [messageDraft, setMessageDraft] = useState("");
  const [messageError, setMessageError] = useState<string | null>(null);
  const [archiveMenuOpen, setArchiveMenuOpen] = useState(false);
  const [archiveMenuPos, setArchiveMenuPos] = useState<AnchoredMenuPanelBox | null>(null);
  const [listStatusError, setListStatusError] = useState<{ id: string; message: string } | null>(null);
  const archiveMenuAnchorRef = useRef<HTMLDivElement | null>(null);

  const openTicket = useCallback(
    (ticketId: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(TICKET_URL_PARAM, ticketId);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeTicket = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(TICKET_URL_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const enabledStatusList = useMemo(
    () => statusKeys.filter((status) => statusEnabled[status]),
    [statusEnabled, statusKeys],
  );

  const closedFilterParam = useMemo((): "true" | "false" | null => {
    if (queueActiveEnabled && queueArchivedEnabled) {
      return null;
    }
    if (queueActiveEnabled) {
      return "false";
    }
    if (queueArchivedEnabled) {
      return "true";
    }
    return null;
  }, [queueActiveEnabled, queueArchivedEnabled]);

  const queueFilterActive = queueActiveEnabled || queueArchivedEnabled;

  const listQuery = useQuery({
    queryKey: [
      "user-admin",
      "hub-support-tickets",
      enabledStatusList.join("|"),
      queueActiveEnabled ? "active" : "",
      queueArchivedEnabled ? "archived" : "",
      showPendingFeedbackOnly ? "pending-feedback" : "all-feedback",
    ],
    enabled: enabledStatusList.length > 0 && queueFilterActive,
    staleTime: 60_000,
    queryFn: () => {
      const allStatusesSelected = enabledStatusList.length === statusKeys.length;
      return apiFetch<{ rows: ListRow[] }>("/api/user-admin/hub-support-tickets", {
        query: {
          ...(allStatusesSelected ? {} : { statuses: enabledStatusList.join(",") }),
          ...(closedFilterParam != null ? { closed: closedFilterParam } : {}),
          ...(showPendingFeedbackOnly ? { pendingFeedback: "true" } : {}),
        },
      });
    },
  });

  const detailQuery = useQuery({
    queryKey: ["user-admin", "hub-support-tickets", "detail", detailId],
    queryFn: () =>
      apiFetch<{
        ticket: DetailTicket;
        screenshots: DetailScreenshot[];
        messages: HubSupportTicketMessage[];
      }>(
        `/api/user-admin/hub-support-tickets/${encodeURIComponent(detailId ?? "")}`,
      ),
    enabled: Boolean(detailId),
  });

  useEffect(() => {
    const ticket = detailQuery.data?.ticket;
    if (!ticket) return;
    setDraftStatus(ticket.status);
    setDraftClosed(ticket.closed);
    setDraftPendingFeedback(ticket.pending_feedback);
    setDraftAdminNotes(ticket.admin_notes ?? "");
    setDraftResolutionNote(ticket.resolution_note ?? "");
    setMessageDraft("");
    setMessageError(null);
    setArchiveMenuOpen(false);
    setArchiveMenuPos(null);
  }, [detailQuery.data?.ticket, detailId]);

  const closeArchiveMenu = useCallback(() => {
    setArchiveMenuOpen(false);
    setArchiveMenuPos(null);
  }, []);

  const updateArchiveMenuPosition = useCallback(() => {
    const anchor = archiveMenuAnchorRef.current;
    if (!anchor) {
      return;
    }
    setArchiveMenuPos(
      computeHorizontalMenuPanel({
        anchorRect: anchor.getBoundingClientRect(),
        menuWidth: 14 * 16,
        horizontalAlign: "end",
        gap: 4,
        preferPlacement: "above",
        maxHeightCap: 160,
      }),
    );
  }, []);

  useLayoutEffect(() => {
    if (!archiveMenuOpen) {
      return;
    }
    updateArchiveMenuPosition();
    const onScrollOrResize = () => {
      updateArchiveMenuPosition();
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.visualViewport?.addEventListener("resize", onScrollOrResize);
    window.visualViewport?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.visualViewport?.removeEventListener("resize", onScrollOrResize);
      window.visualViewport?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [archiveMenuOpen, updateArchiveMenuPosition]);

  useEffect(() => {
    if (!detailId) {
      closeArchiveMenu();
    }
  }, [detailId, closeArchiveMenu]);

  useEffect(() => {
    if (!archiveMenuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.("[data-ticket-archive-menu-panel], [data-ticket-archive-menu-anchor]")) {
        return;
      }
      closeArchiveMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeArchiveMenu();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [archiveMenuOpen, closeArchiveMenu]);

  const patchMutation = useMutation({
    mutationFn: (input: {
      id: string;
      status?: string;
      closed?: boolean;
      pendingFeedback?: boolean;
      adminNotes: string | null;
      resolutionNote: string | null;
    }) =>
      apiFetch<{ ticket: { id: string } }>(`/api/user-admin/hub-support-tickets/${encodeURIComponent(input.id)}`, {
        method: "PATCH",
        body: {
          ...(input.status !== undefined ? { status: input.status } : {}),
          ...(input.closed !== undefined ? { closed: input.closed } : {}),
          ...(input.pendingFeedback !== undefined ? { pendingFeedback: input.pendingFeedback } : {}),
          adminNotes: input.adminNotes,
          resolutionNote: input.resolutionNote,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "hub-support-tickets"] });
      if (detailId) {
        void detailQuery.refetch();
      }
    },
  });

  const listStatusMutation = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      apiFetch<{ ticket: { id: string } }>(`/api/user-admin/hub-support-tickets/${encodeURIComponent(input.id)}`, {
        method: "PATCH",
        body: { status: input.status },
      }),
    onSuccess: () => {
      setListStatusError(null);
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "hub-support-tickets"] });
      if (detailId) {
        void detailQuery.refetch();
      }
    },
    onError: (error: Error, variables) => {
      setListStatusError({ id: variables.id, message: error.message });
    },
  });

  const messageMutation = useMutation({
    mutationFn: (input: { id: string; body: string; attachments: HubSupportLocalAttachment[] }) =>
      apiFetch<{ ok: boolean; messages: HubSupportTicketMessage[] }>(
        `/api/user-admin/hub-support-tickets/${encodeURIComponent(input.id)}/messages`,
        {
          method: "POST",
          body: {
            body: input.body,
            attachments: input.attachments.map((row) => hubSupportTicketAttachmentApiPayload(row)),
          },
        },
      ),
    onSuccess: async (result) => {
      setMessageDraft("");
      setMessageError(null);
      queryClient.setQueryData(
        ["user-admin", "hub-support-tickets", "detail", detailId],
        (previous:
          | { ticket: DetailTicket; screenshots: DetailScreenshot[]; messages: HubSupportTicketMessage[] }
          | undefined) => (previous ? { ...previous, messages: result.messages } : previous),
      );
      await queryClient.invalidateQueries({ queryKey: ["user-admin", "hub-support-tickets"] });
    },
    onError: (error: Error) => {
      setMessageError(error.message);
    },
  });

  const invalidateTicketOwnership = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["user-admin", "hub-support-tickets"] });
  }, [queryClient]);

  const rowsRaw = listQuery.data?.rows ?? [];
  const sortedRows = useMemo(() => {
    const copy = [...rowsRaw];
    copy.sort((left, right) => compareTicketRows(left, right, sortKey, sortDir));
    return copy;
  }, [rowsRaw, sortDir, sortKey]);

  const ticketColumns = useMemo((): HubDataTableColumn<ListRow, TicketSortKey>[] => {
    return [
      {
        id: "id",
        header: "Ticket",
        sortKey: "id",
        overflow: "nowrap",
        sortValue: (row) => readTicketSortValue(row, "id"),
        cell: (row) => <span className="font-medium tabular-nums text-slate-900">#{row.id}</span>,
      },
      {
        id: "subject",
        header: "Subject",
        sortKey: "subject",
        sortValue: (row) => row.subject ?? "",
        cell: (row) =>
          row.subject ? (
            <button
              type="button"
              className="text-left font-medium text-brand-700 underline decoration-brand-300 hover:text-brand-900"
              onClick={() => openTicket(row.id)}
            >
              {row.subject}
            </button>
          ) : (
            <button
              type="button"
              className="text-left text-slate-400 hover:text-slate-600"
              onClick={() => openTicket(row.id)}
            >
              —
            </button>
          ),
      },
      {
        id: "created_at",
        header: "Created",
        sortKey: "created_at",
        overflow: "nowrap",
        sortValue: (row) => row.created_at,
        cell: (row) => formatTimestamp(row.created_at),
      },
      {
        id: "reporter_display_name",
        header: "Reporter",
        sortKey: "reporter_display_name",
        overflow: "truncate",
        sortValue: (row) => row.reporter_display_name,
        cell: (row) => (
          <HubDataTextCell
            overflow="truncate"
            value={
              row.submission_source === "automatic_client"
                ? `Automatic for ${row.reporter_display_name}`
                : row.submission_source === "system_automation"
                  ? "System automation"
                  : row.reporter_display_name
            }
            className="text-slate-800"
          />
        ),
      },
      {
        id: "owner_display_name",
        header: "Owner",
        sortKey: "owner_display_name",
        overflow: "truncate",
        sortValue: (row) => row.owner_display_name ?? "",
        cell: (row) => (
          <HubDataTextCell
            overflow="truncate"
            value={formatNotificationItemOwnerLabel(row.owner_user_id, row.owner_display_name)}
            className="text-slate-700"
          />
        ),
      },
      {
        id: "kind",
        header: "Kind",
        sortKey: "kind",
        sortValue: (row) => row.kind,
        cell: (row) => row.kind,
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (row) => row.status,
        cell: (row) => (
          <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
              <select
                className="max-w-[11rem] rounded-md border border-slate-200 bg-white px-2 py-1 text-sm disabled:opacity-60"
                value={row.status}
                disabled={
                  !hubWritesAllowed || (listStatusMutation.isPending && listStatusMutation.variables?.id === row.id)
                }
                aria-label={`Status for ticket ${row.id}`}
                onChange={(event) => {
                  const nextStatus = event.target.value;
                  if (nextStatus === row.status) {
                    return;
                  }
                  setListStatusError(null);
                  listStatusMutation.mutate({ id: row.id, status: nextStatus });
                }}
              >
                {statusMeta.map((statusRow) => (
                  <option key={statusRow.status} value={statusRow.status} title={statusRow.description}>
                    {formatHubSupportTicketStatusLabel(statusRow.status)}
                  </option>
                ))}
              </select>
            </ReadOnlyHoverLayer>
            {row.pending_feedback ? (
              <span className="mt-1 block text-xs font-medium text-cyan-700">pending feedback</span>
            ) : null}
            {listStatusError?.id === row.id ? (
              <span className="mt-1 block text-xs text-rose-700">{listStatusError.message}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: "page_path",
        header: "Page",
        sortKey: "page_path",
        overflow: "truncate",
        sortValue: (row) => row.page_path ?? "",
        headerClassName: "w-[16rem]",
        cellClassName: "w-0 max-w-[16rem] font-mono text-xs text-slate-600",
        cell: (row) => (
          <div className="w-[16rem] max-w-[16rem]">
            <HubDataTextCell overflow="truncate" value={row.page_path ?? "—"} />
          </div>
        ),
      },
    ];
  }, [
    adminReadOnlyTitle,
    hubWritesAllowed,
    listStatusError,
    listStatusMutation,
    openTicket,
    statusMeta,
  ]);

  function toggleTicketSort(column: TicketSortKey): void {
    if (sortKey === column) {
      setSortDir((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column);
    setSortDir(column === "created_at" || column === "id" ? "desc" : "asc");
  }

  const queueFiltersActive =
    showPendingFeedbackOnly
    || !queueActiveEnabled
    || !queueArchivedEnabled
    || enabledStatusList.length !== statusKeys.length;

  const detail = detailQuery.data;
  const selectedStatusMeta = statusMeta.find((row) => row.status === draftStatus) ?? null;
  const draftAllowsPendingFeedback = hubSupportTicketAllowsPendingFeedback({
    status: draftStatus,
    closed: draftClosed,
  });
  const draftMayArchive = hubSupportTicketStatusMayClose(statusMeta, draftStatus);
  const ticketDirty = useMemo(() => {
    const ticket = detail?.ticket;
    if (!ticket) {
      return false;
    }
    return (
      draftStatus !== ticket.status ||
      draftClosed !== ticket.closed ||
      draftPendingFeedback !== ticket.pending_feedback ||
      draftAdminNotes.trim() !== (ticket.admin_notes ?? "").trim() ||
      draftResolutionNote.trim() !== (ticket.resolution_note ?? "").trim()
    );
  }, [
    detail?.ticket,
    draftAdminNotes,
    draftClosed,
    draftPendingFeedback,
    draftResolutionNote,
    draftStatus,
  ]);

  const buildPatchPayload = (input?: {
    status?: string;
    closed?: boolean;
    pendingFeedback?: boolean;
  }) => {
    const trimmedAdminNotes = draftAdminNotes.trim();
    const trimmedResolutionNote = draftResolutionNote.trim();
    return {
      ...(input?.status !== undefined ? { status: input.status } : { status: draftStatus }),
      ...(input?.closed !== undefined ? { closed: input.closed } : { closed: draftClosed }),
      ...(input?.pendingFeedback !== undefined
        ? { pendingFeedback: input.pendingFeedback }
        : { pendingFeedback: draftPendingFeedback }),
      adminNotes: trimmedAdminNotes === "" ? null : trimmedAdminNotes,
      resolutionNote: trimmedResolutionNote === "" ? null : trimmedResolutionNote,
    };
  };

  const saveTicket = async (input?: {
    status?: string;
    closed?: boolean;
    pendingFeedback?: boolean;
    closeModalAfter?: boolean;
  }) => {
    const ticketId = detailId;
    if (!ticketId) return;
    closeArchiveMenu();
    await patchMutation.mutateAsync({
      id: ticketId,
      ...buildPatchPayload(input),
    });
    if (input?.closeModalAfter) {
      closeTicket();
    }
  };

  const archiveWithTerminalStatus = async (status: "resolved" | "declined") => {
    await saveTicket({
      status,
      closed: true,
      pendingFeedback: false,
      closeModalAfter: true,
    });
  };

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Bug reports and suggestions submitted from the account menu → Support. Workflow status tracks progress; Archive (closed axis) means no human review is required. Only resolved or declined tickets may be archived. Pending feedback marks tickets waiting on the reporter. When status becomes resolved or declined, the reporter receives an email with the resolution note.">
          Tickets
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Queue</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
            {statusMeta.map((row) => {
              const isOn = statusEnabled[row.status] ?? false;
              return (
                <Button
                  key={row.status}
                  type="button"
                  size="sm"
                  variant={isOn ? "primary" : "secondary"}
                  title={row.description}
                  onClick={() =>
                    setStatusEnabled((previous) => ({
                      ...previous,
                      [row.status]: !previous[row.status],
                    }))
                  }
                >
                  {formatHubSupportTicketStatusLabel(row.status)}
                </Button>
              );
            })}
            <span className="mx-1 text-slate-300">|</span>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Queue</span>
            <Button
              type="button"
              size="sm"
              variant={queueActiveEnabled ? "primary" : "secondary"}
              title="Tickets still in the review queue (not archived)."
              onClick={() => setQueueActiveEnabled((previous) => !previous)}
            >
              Active
            </Button>
            <Button
              type="button"
              size="sm"
              variant={queueArchivedEnabled ? "primary" : "secondary"}
              title="Tickets archived from the review queue (closed axis)."
              onClick={() => setQueueArchivedEnabled((previous) => !previous)}
            >
              Archived
            </Button>
            <Button
              type="button"
              size="sm"
              variant={showPendingFeedbackOnly ? "primary" : "secondary"}
              title="Only tickets waiting on reporter feedback."
              onClick={() => setShowPendingFeedbackOnly((previous) => !previous)}
            >
              Pending feedback
            </Button>
          </div>
          {enabledStatusList.length === 0 ? (
            <p className="text-sm text-slate-600">Turn on at least one status above to load tickets.</p>
          ) : !queueFilterActive ? (
            <p className="text-sm text-slate-600">Turn on Active or Archived above to load tickets.</p>
          ) : (
            <HubDataTable
              fillViewport={false}
              className="max-h-[70vh] border-0 shadow-none ring-0"
              columns={ticketColumns}
              rows={sortedRows}
              rowKey={(row) => row.id}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleTicketSort}
              loading={listQuery.isLoading}
              loadingMessage="Loading…"
              error={listQuery.isError ? (listQuery.error as Error).message : null}
              empty="No tickets in queue."
              isFiltered={queueFiltersActive}
              filteredEmpty="No tickets match this filter."
            />
          )}
        </CardBody>
      </Card>

      {detailId ? (
        <DocumentModal
          title={`Ticket ${detail?.ticket?.id ?? detailId}`}
          showCloseButton={false}
          onClose={closeTicket}
          shellClassName="max-h-[min(92vh,56rem)] max-w-4xl"
          mainClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
          description={
            detail?.ticket ? (
              <span className="text-slate-600">
                {detail.ticket.submission_source === "automatic_client"
                  ? `Automatically detected for ${detail.ticket.reporter_display_name}`
                  : detail.ticket.submission_source === "system_automation"
                    ? "Created by system automation"
                    : `Submitted by ${detail.ticket.reporter_display_name}`}{" "}
                · {detail.ticket.kind} · opened{" "}
                {formatTimestamp(detail.ticket.created_at)}
              </span>
            ) : undefined
          }
        >
          {detailQuery.isLoading && !detail?.ticket ? (
            <p className="px-6 py-5 text-sm text-slate-500">Loading…</p>
          ) : detailQuery.isError ? (
            <p className="px-6 py-5 text-sm text-rose-700">{(detailQuery.error as Error).message}</p>
          ) : detail?.ticket ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                    <select
                      className="w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-sm"
                      value={draftStatus}
                      disabled={!hubWritesAllowed}
                      onChange={(event) => {
                        const nextStatus = event.target.value;
                        setDraftStatus(nextStatus);
                        if (!hubSupportTicketStatusMayClose(statusMeta, nextStatus)) {
                          setDraftClosed(false);
                        }
                        if (!hubSupportTicketAllowsPendingFeedback({ status: nextStatus, closed: draftClosed })) {
                          setDraftPendingFeedback(false);
                        }
                      }}
                    >
                      {statusMeta.map((row) => (
                        <option key={row.status} value={row.status} title={row.description}>
                          {formatHubSupportTicketStatusLabel(row.status)}
                        </option>
                      ))}
                    </select>
                  </ReadOnlyHoverLayer>
                  {selectedStatusMeta ? (
                    <p className="mt-2 text-xs text-slate-600">{selectedStatusMeta.description}</p>
                  ) : null}
                  <div className="mt-3 space-y-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={draftPendingFeedback}
                        disabled={!hubWritesAllowed || !draftAllowsPendingFeedback}
                        onChange={(event) => setDraftPendingFeedback(event.target.checked)}
                      />
                      <span>
                        Pending feedback
                        {!draftAllowsPendingFeedback ? (
                          <span className="block text-xs text-slate-500">
                            Not available when archived or status is resolved/declined.
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </div>
                </div>
                <div>
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    Last status change
                  </span>
                  <p className="text-sm text-slate-700">
                    {detail.ticket.last_status_change_at ?? "—"}
                    {detail.ticket.last_status_actor_user_id ? (
                      <span
                        className="block text-xs text-slate-500"
                        title={detail.ticket.last_status_actor_user_id}
                      >
                        {detail.ticket.last_status_actor_display_name ?? detail.ticket.last_status_actor_user_id}
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Owner
                </span>
                <NotificationItemOwnerControls
                  topic="support_ticket"
                  sourceId={detail.ticket.id}
                  ownerUserId={detail.ticket.owner_user_id}
                  ownerDisplayName={detail.ticket.owner_display_name}
                  onOwnershipChanged={invalidateTicketOwnership}
                />
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Internal notes
                </span>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
                  <textarea
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    rows={3}
                    disabled={!hubWritesAllowed}
                    value={draftAdminNotes}
                    onChange={(event) => setDraftAdminNotes(event.target.value)}
                    placeholder="Visible to admins only."
                  />
                </ReadOnlyHoverLayer>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Resolution note (reporter-facing)
                </span>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
                  <textarea
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    rows={3}
                    disabled={!hubWritesAllowed}
                    value={draftResolutionNote}
                    onChange={(event) => setDraftResolutionNote(event.target.value)}
                    placeholder="Shown to the reporter in Support and included in email when status becomes resolved or declined."
                  />
                </ReadOnlyHoverLayer>
              </div>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
                <HubSupportTicketMessageThread
                  messages={detail.messages ?? []}
                  draft={messageDraft}
                  onDraftChange={setMessageDraft}
                  submitting={messageMutation.isPending}
                  canReply={hubWritesAllowed}
                  allowAttachments={hubWritesAllowed}
                  error={messageError}
                  composeLabel="Send message"
                  viewer="admin"
                  onSubmit={({ attachments }) => {
                    if (!detailId) return;
                    const trimmed = messageDraft.trim();
                    if (trimmed === "" && attachments.length === 0) return;
                    messageMutation.mutate({ id: detailId, body: trimmed, attachments });
                  }}
                />
              </ReadOnlyHoverLayer>
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Page</span>
                <p className="break-all font-mono text-xs text-slate-700">{detail.ticket.page_url_full ?? "—"}</p>
                <p className="mt-1 break-all font-mono text-xs text-slate-500">{detail.ticket.user_agent ?? ""}</p>
              </div>
              <div>
                <span className={hubSupportTicketFieldLabelClassName(detail.ticket.kind)}>
                  {hubSupportTicketReviewerFieldCopy(detail.ticket.kind).actualLabel}
                </span>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-800">
                  {detail.ticket.actual_behavior}
                </pre>
              </div>
              <div>
                <span className={hubSupportTicketFieldLabelClassName(detail.ticket.kind)}>
                  {hubSupportTicketReviewerFieldCopy(detail.ticket.kind).expectedLabel}
                </span>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-800">
                  {detail.ticket.expected_behavior}
                </pre>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Console</span>
                <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 font-mono text-[11px] text-slate-700">
                  {detail.ticket.console_capture?.trim() === "(No console output captured yet in this tab.)"
                    ? "(none)"
                    : (detail.ticket.console_capture ?? "(none)")}
                </pre>
              </div>
              {detail.ticket.cursor_agent_status ? (
                <div className="rounded-md border border-slate-200 bg-slate-50/80 p-4">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Cursor agent
                    </span>
                    <HoverHintWithGlyph
                      hoverHint="Estimated cost uses Cursor Models pool list rates from streamed token counts (or inferred input when the stream omits it). Included plan usage is still shown at list price. Actual billing is on your Cursor usage dashboard."
                    />
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                      {formatHubSupportTicketStatusLabel(detail.ticket.cursor_agent_status)}
                    </span>
                  </div>
                  <dl className="grid gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-slate-500">Model</dt>
                      <dd className="text-slate-800">{detail.ticket.cursor_agent_model ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Est. cost</dt>
                      <dd className="text-slate-800">
                        {formatCursorAgentCostUsd(
                          detail.ticket.cursor_agent_cost_usd,
                          detail.ticket.cursor_agent_input_tokens,
                          detail.ticket.cursor_agent_output_tokens,
                          detail.ticket.cursor_agent_usage_quality,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Duration</dt>
                      <dd className="text-slate-800">
                        {formatCursorAgentDurationMs(detail.ticket.cursor_agent_duration_ms)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Completed</dt>
                      <dd className="text-slate-800">{detail.ticket.cursor_agent_completed_at ?? "—"}</dd>
                    </div>
                  </dl>
                  {(() => {
                    const tokenSummary = formatCursorAgentTokenSummary({
                      inputTokens: detail.ticket.cursor_agent_input_tokens,
                      outputTokens: detail.ticket.cursor_agent_output_tokens,
                      cacheReadTokens: detail.ticket.cursor_agent_cache_read_tokens,
                      usageQuality: detail.ticket.cursor_agent_usage_quality,
                    });
                    const usageFootnote = formatCursorAgentUsageFootnote(
                      detail.ticket.cursor_agent_usage_quality,
                    );
                    return (
                      <>
                        {tokenSummary ? (
                          <p className="mt-2 font-mono text-[11px] text-slate-600">{tokenSummary}</p>
                        ) : detail.ticket.cursor_agent_status === "completed" ||
                          detail.ticket.cursor_agent_status === "failed" ? (
                          <p className="mt-2 text-[11px] text-slate-500">
                            Token usage was not reported on the agent stream.
                          </p>
                        ) : null}
                        {usageFootnote ? (
                          <p className="mt-1 text-[11px] text-slate-500">{usageFootnote}</p>
                        ) : null}
                      </>
                    );
                  })()}
                  {detail.ticket.cursor_agent_error_message ? (
                    <p className="mt-2 text-sm text-rose-700">{detail.ticket.cursor_agent_error_message}</p>
                  ) : null}
                  {detail.ticket.cursor_agent_tldr_problem ||
                  detail.ticket.cursor_agent_tldr_cause ||
                  detail.ticket.cursor_agent_tldr_fix ? (
                    <dl className="mt-3 grid gap-3 rounded border border-slate-100 bg-white p-3 text-sm">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Problem</dt>
                        <dd className="mt-1 text-slate-800">
                          {detail.ticket.cursor_agent_tldr_problem ?? "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Cause</dt>
                        <dd className="mt-1 text-slate-800">{detail.ticket.cursor_agent_tldr_cause ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Fix</dt>
                        <dd className="mt-1 text-slate-800">{detail.ticket.cursor_agent_tldr_fix ?? "—"}</dd>
                      </div>
                    </dl>
                  ) : null}
                  {detail.ticket.cursor_agent_summary_detail ? (
                    <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-slate-100 bg-white p-3 text-xs text-slate-800">
                      {detail.ticket.cursor_agent_summary_detail}
                    </pre>
                  ) : detail.ticket.cursor_agent_summary ? (
                    <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-slate-100 bg-white p-3 text-xs text-slate-800">
                      {detail.ticket.cursor_agent_summary}
                    </pre>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    {isLocalCursorAgentId(detail.ticket.cursor_agent_id) ? (
                      <span className="inline-flex flex-wrap items-center gap-1.5 text-slate-700">
                        <span>In Cursor, say</span>
                        <code className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-800 ring-1 ring-slate-200">
                          {localAgentResumeKeyphrase(detail.ticket.id)}
                        </code>
                        <CopyToClipboardGlyph
                          text={localAgentResumeKeyphrase(detail.ticket.id)}
                          label="resume phrase"
                        />
                        <a
                          href={localAgentResumeDeeplink(detail.ticket.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-brand-700 underline decoration-brand-300 hover:text-brand-900"
                        >
                          Open in Cursor
                        </a>
                      </span>
                    ) : detail.ticket.cursor_agent_url ? (
                      <a
                        href={detail.ticket.cursor_agent_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand-700 underline decoration-brand-300 hover:text-brand-900"
                      >
                        Open in Cursor
                      </a>
                    ) : null}
                    {detail.ticket.cursor_agent_pr_url ? (
                      <a
                        href={detail.ticket.cursor_agent_pr_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand-700 underline decoration-brand-300 hover:text-brand-900"
                      >
                        Pull request
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {detail.screenshots.length > 0 ? (
                <div>
                  <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    Attached when opened
                  </span>
                  <HubSupportTicketAttachmentList
                    attachments={detail.screenshots}
                    thumbnailClassName="pointer-events-none h-32 w-full object-cover"
                  />
                </div>
              ) : null}
              </div>
              {patchMutation.isError ? (
                <p className="px-6 text-sm text-rose-700">
                  {patchMutation.error instanceof ApiError
                    ? patchMutation.error.message
                    : (patchMutation.error as Error).message}
                </p>
              ) : null}
              <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
                <Button type="button" variant="secondary" onClick={closeTicket}>
                  Cancel
                </Button>
                <HubSaveButton
                  type="button"
                  dirty={ticketDirty}
                  disabled={!hubWritesAllowed}
                  blockedHint={adminReadOnlyTitle}
                  isLoading={patchMutation.isPending}
                  onClick={() => {
                    void saveTicket().catch(() => undefined);
                  }}
                >
                  Save changes
                </HubSaveButton>
                {!draftClosed ? (
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                    {draftMayArchive ? (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={!hubWritesAllowed || patchMutation.isPending}
                        onClick={() => {
                          void saveTicket({ closed: true, closeModalAfter: true }).catch(() => undefined);
                        }}
                      >
                        {patchMutation.isPending ? "Archiving…" : "Archive"}
                      </Button>
                    ) : (
                      <div
                        ref={archiveMenuAnchorRef}
                        data-ticket-archive-menu-anchor
                        className="inline-flex"
                      >
                        <Button
                          type="button"
                          variant="primary"
                          disabled={!hubWritesAllowed || patchMutation.isPending}
                          aria-haspopup="menu"
                          aria-expanded={archiveMenuOpen}
                          onClick={() => {
                            setArchiveMenuOpen((previous) => !previous);
                          }}
                        >
                          {patchMutation.isPending ? "Archiving…" : "Archive"}
                          <ChevronUp className="ml-1 h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    )}
                  </ReadOnlyHoverLayer>
                ) : null}
                {draftClosed ? (
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!hubWritesAllowed || patchMutation.isPending}
                      onClick={() => {
                        void saveTicket({ closed: false }).catch(() => undefined);
                      }}
                    >
                      Reopen for review
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="px-6 py-5 text-sm text-slate-500">No data.</p>
          )}
        </DocumentModal>
      ) : null}
      {detailId && !draftClosed && !draftMayArchive && archiveMenuOpen && archiveMenuPos
        ? createPortal(
            <ul
              data-ticket-archive-menu-panel
              role="menu"
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm text-slate-800 shadow-lg"
              style={{
                top: archiveMenuPos.top,
                left: archiveMenuPos.left,
                width: archiveMenuPos.width,
                maxHeight: archiveMenuPos.maxHeight,
                transform: archiveMenuPos.transform,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              <li>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                  disabled={!hubWritesAllowed || patchMutation.isPending}
                  onClick={() => {
                    void archiveWithTerminalStatus("resolved").catch(() => undefined);
                  }}
                >
                  Resolved and closed
                </button>
              </li>
              <li>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                  disabled={!hubWritesAllowed || patchMutation.isPending}
                  onClick={() => {
                    void archiveWithTerminalStatus("declined").catch(() => undefined);
                  }}
                >
                  Declined and closed
                </button>
              </li>
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
